import { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@rocco/utils/cn';
import type { ContactSuggestion, DirectConversation } from '../../../types';
import { useChat } from '../../../hooks/use-chat';
import { useMigrateMessagesPreview } from '../../../hooks/use-migrate-messages-preview';
import { useUpdateConversation } from '../../../hooks/use-update-conversation';
import { ChatBoxSettingsMembersList } from './ChatBoxSettingsMembersList';
import { AddParticipantInput } from './AddParticipantInput';
import { ChatBoxSettingsButton } from './ChatBoxSettingsButton';
import { InviteToGroupChatDialog } from './InviteToGroupChatDialog';

/**
 * The "Members" tab content of the chat box conversation settings.
 * @see {@link ChatBoxSettings}
 */
export function ChatBoxSettingsMembers({
    className,
    conversation,
    onClose,
}: {
    className?: string;
    conversation: DirectConversation;
    onClose?: () => void;
}) {
    const [isAddingParticipant, setIsAddingParticipant] = useState(false);
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const [participantsToAdd, setParticipantsToAdd] = useState<
        ContactSuggestion[]
    >([]);
    const { openChatBox } = useChat();
    const { clearCache: clearMigrateMessagesPreviewCache } =
        useMigrateMessagesPreview({
            conversationID: 0,
            fromTimestamp: null,
        });
    const {
        isSubmitting,
        addParticipants,
        removeParticipant,
        inviteToGroupChat,
    } = useUpdateConversation({
        conversationID: Number(conversation.ID),
        onAddSuccess: () => {
            setParticipantsToAdd([]);
            onClose?.();
        },
        onRemoveSuccess: onClose,
        onInviteToGroupChatSuccess: (groupChatID) => {
            clearMigrateMessagesPreviewCache();
            setIsInviteDialogOpen(false);
            openChatBox(groupChatID);
            onClose?.();
        },
        onInviteToGroupChatError: (message) => {
            const errorMessage =
                message === 'One or more participants are not in your contacts'
                    ? `${message} (you may have been blocked)`
                    : message;
            toast.error(errorMessage);
        },
    });

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            {!isAddingParticipant ? (
                <>
                    <button
                        className="flex h-[52px] w-full items-center gap-[8px] rounded-full p-[8px] hover:bg-white/50 disabled:pointer-events-none disabled:opacity-50"
                        disabled={isSubmitting}
                        onClick={() => {
                            if (conversation.IsGroupChat) {
                                setIsAddingParticipant(true);
                            } else {
                                setIsInviteDialogOpen(true);
                            }
                        }}
                    >
                        <PlusIcon
                            className="size-[36px] shrink-0 rounded-full border border-black p-[4px]"
                            strokeWidth="1.5px"
                        />
                        <span className="min-w-0 flex-1 break-words text-left text-[14px] font-medium leading-tight">
                            Add people to this conversation
                        </span>
                    </button>
                    <ChatBoxSettingsMembersList
                        conversation={conversation}
                        isDisabled={isSubmitting}
                        onRemoveParticipant={removeParticipant}
                    />
                </>
            ) : (
                <AddParticipantInput
                    className="min-h-0 flex-1"
                    inputClassName="h-[52px]"
                    participants={conversation.Participants}
                    participantsToAdd={participantsToAdd}
                    isDisabled={isSubmitting}
                    onParticipantsChange={setParticipantsToAdd}
                />
            )}
            <div className="mt-auto flex items-center justify-end gap-[4px]">
                {isAddingParticipant && (
                    <ChatBoxSettingsButton
                        className="hover:bg-white/50"
                        color="transparent"
                        disabled={isSubmitting}
                        onClick={() => {
                            setIsAddingParticipant(false);
                            setParticipantsToAdd([]);
                        }}
                    >
                        Back
                    </ChatBoxSettingsButton>
                )}
                <ChatBoxSettingsButton
                    disabled={isSubmitting}
                    isLoading={isSubmitting}
                    onClick={() => {
                        if (isAddingParticipant) {
                            addParticipants(participantsToAdd.map((p) => p.ID));
                        } else {
                            onClose?.();
                        }
                    }}
                >
                    Save & Close
                </ChatBoxSettingsButton>
            </div>

            <InviteToGroupChatDialog
                open={isInviteDialogOpen}
                conversation={conversation}
                isSubmitting={isSubmitting}
                onOpenChange={setIsInviteDialogOpen}
                onConfirm={inviteToGroupChat}
            />
        </div>
    );
}
